import { differenceInCalendarDays } from 'date-fns';

/** Groupes de la liste des tâches d'un dossier, du plus pressant au moins pressant. */
export type TaskUrgencyGroup = 'late' | 'week' | 'later' | 'done';

export const TASK_URGENCY_GROUPS: readonly { key: TaskUrgencyGroup; label: string }[] = [
  { key: 'late', label: 'En retard' },
  { key: 'week', label: 'Cette semaine' },
  { key: 'later', label: 'Plus tard' },
  { key: 'done', label: 'Terminées' },
];

interface UrgencyTask {
  is_completed: boolean | null;
  deadline: string | null;
  due_date: string | null;
}

export function taskDueIso(task: Pick<UrgencyTask, 'deadline' | 'due_date'>): string | null {
  return task.deadline ?? task.due_date;
}

/** Jours entre aujourd'hui et l'échéance (négatif = en retard). `null` sans échéance. */
export function taskDaysLeft(task: Pick<UrgencyTask, 'deadline' | 'due_date'>, now: Date): number | null {
  const iso = taskDueIso(task);
  return iso ? differenceInCalendarDays(new Date(iso), now) : null;
}

export function taskUrgencyGroup(task: UrgencyTask, now: Date): TaskUrgencyGroup {
  if (task.is_completed) return 'done';
  const days = taskDaysLeft(task, now);
  if (days === null) return 'later';
  if (days < 0) return 'late';
  return days <= 7 ? 'week' : 'later';
}

/** Tâches rangées par groupe ; dans un groupe, la plus proche d'abord, sans date à la fin. */
export function groupTasksByUrgency<T extends UrgencyTask>(
  tasks: readonly T[],
  now: Date,
): Record<TaskUrgencyGroup, T[]> {
  const groups: Record<TaskUrgencyGroup, T[]> = { late: [], week: [], later: [], done: [] };
  for (const task of tasks) groups[taskUrgencyGroup(task, now)].push(task);
  const time = (task: T) => {
    const iso = taskDueIso(task);
    return iso ? new Date(iso).getTime() : Number.POSITIVE_INFINITY;
  };
  for (const key of ['late', 'week', 'later'] as const) groups[key].sort((a, b) => time(a) - time(b));
  groups.done.sort((a, b) => time(b) - time(a));
  return groups;
}

/** « il y a 2 j », « aujourd'hui », « dans 3 j ». */
export function relativeDueLabel(days: number): string {
  if (days === 0) return "aujourd'hui";
  if (days === 1) return 'demain';
  if (days === -1) return 'hier';
  return days < 0 ? `il y a ${-days} j` : `dans ${days} j`;
}
